import React, { useState, useCallback } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useUpdateOpportunity, useDeleteOpportunity } from "@/hooks/queries";
import { useNotification } from "@/hooks/useNotification";
import type { Opportunity, OpportunitySubcategory } from "@api/opportunity";
import type { ColumnDef } from "./columnConfig";
import { getDisplayValue, isCellReadOnly } from "./opportunityCells";
import { SubcategorySelector } from "./SubcategorySelector";
import styles from "./styles.module.css";

type Props = {
  opportunity: Opportunity;
  isAdmin: boolean;
  columns: ColumnDef[];
  subcategories: OpportunitySubcategory[];
};

type EditValues = {
  primary_date: string | null;
  subcategory_id: string | null;
  fields: Record<string, string | null>;
};

const buildEditValues = (opp: Opportunity): EditValues => ({
  primary_date: opp.primary_date,
  subcategory_id: opp.subcategory_id,
  fields: { ...opp.fields },
});

const getEditValue = (editValues: EditValues, col: ColumnDef): string | null => {
  if (col.isPrimarySort) return editValues.primary_date;
  return editValues.fields[col.field] ?? null;
};

export const OpportunityRow = ({ opportunity, isAdmin, columns, subcategories }: Props) => {
  const updateOpportunityMutation = useUpdateOpportunity();
  const deleteOpportunityMutation = useDeleteOpportunity();
  const { notifySuccess, notifyError } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>(buildEditValues(opportunity));
  const hasSubcategories = subcategories.length > 0;
  // Non-admins can edit a row only when the category has at least one Open-Edit column; within edit
  // mode, isCellReadOnly() still restricts them to those specific cells. Delete stays admin-only.
  const canEdit = isAdmin || columns.some((c) => c.openEdit);

  const handleEdit = useCallback(() => {
    setEditValues(buildEditValues(opportunity));
    setIsEditing(true);
  }, [opportunity]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(() => {
    updateOpportunityMutation.mutate(
      {
        id: opportunity.id,
        payload: {
          primary_date: editValues.primary_date,
          subcategory_id: editValues.subcategory_id,
          fields: editValues.fields,
        },
      },
      {
        onSuccess: () => {
          notifySuccess("Opportunity updated");
          setIsEditing(false);
        },
        onError: () => notifyError("Failed to update opportunity"),
      }
    );
  }, [opportunity.id, editValues, updateOpportunityMutation, notifySuccess, notifyError]);

  const handleDelete = useCallback(() => {
    if (window.confirm("Are you sure you want to delete this opportunity?")) {
      deleteOpportunityMutation.mutate(opportunity.id, {
        onSuccess: () => notifySuccess("Opportunity deleted"),
        onError: () => notifyError("Failed to delete opportunity"),
      });
    }
  }, [opportunity.id, deleteOpportunityMutation, notifySuccess, notifyError]);

  const handleFieldChange = useCallback(
    (field: string, isPrimarySort: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isPrimarySort) {
        setEditValues((prev) => ({ ...prev, primary_date: e.target.value }));
      } else {
        setEditValues((prev) => ({
          ...prev,
          fields: { ...prev.fields, [field]: e.target.value },
        }));
      }
    },
    []
  );

  const handleDateChange = useCallback(
    (field: string, isPrimarySort: boolean) => (newVal: Dayjs | null) => {
      const value = newVal ? newVal.toISOString() : null;
      if (isPrimarySort) {
        setEditValues((prev) => ({ ...prev, primary_date: value }));
      } else {
        setEditValues((prev) => ({
          ...prev,
          fields: { ...prev.fields, [field]: value },
        }));
      }
    },
    []
  );

  if (isEditing && canEdit) {
    return (
      <>
        {columns.map((col) => (
          <td key={col.field}>
            {isCellReadOnly(opportunity, col, isAdmin) ? (
              getDisplayValue(opportunity, col)
            ) : col.isDate ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={
                    getEditValue(editValues, col)
                      ? dayjs(getEditValue(editValues, col))
                      : null
                  }
                  onChange={handleDateChange(col.field, !!col.isPrimarySort)}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "standard",
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            ) : (
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={getEditValue(editValues, col) ?? ""}
                onChange={handleFieldChange(col.field, !!col.isPrimarySort)}
              />
            )}
          </td>
        ))}
        <td className={styles.actionsCell}>
          {hasSubcategories && isAdmin && (
            <SubcategorySelector
              value={editValues.subcategory_id}
              onChange={(sub) => setEditValues((prev) => ({ ...prev, subcategory_id: sub }))}
              disabled={updateOpportunityMutation.isPending}
              subcategories={subcategories}
            />
          )}
          <IconButton size="small" onClick={handleSave} color="primary" disabled={updateOpportunityMutation.isPending || (isAdmin && hasSubcategories && !editValues.subcategory_id)}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel} disabled={updateOpportunityMutation.isPending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </td>
      </>
    );
  }

  return (
    <>
      {columns.map((col) => (
        <td key={col.field}>{getDisplayValue(opportunity, col)}</td>
      ))}
      {canEdit && (
        <td className={styles.actionsCell}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={handleEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={handleDelete} color="error">
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </td>
      )}
    </>
  );
};
